import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import BottomNavigation from '../Mui/BottomNavigation';
import BottomNavigationAction from '../Mui/BottomNavigationAction';
import ExternalLink from '../ExternalLink';
import useNavigateWithSearch from '../../../../hooks/useNavigateWithSearch';

import trashieLogo from '../../images/trashie-logo.webp';
import instagramLogo from '../../images/instagram-logo.svg';
import tiktokLogo from '../../images/tiktok-logo.svg';
import facebookLogo from '../../images/facebook-logo.svg';

import { isMobile } from '../../../../hooks/useBreakPoint';
import {
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
  TRASHIE_SOCIAL_NETWORKS,
  TAKE_BACK_BAG_URL,
  PARTNERSHIPS_URL,
  CONTACT_US_URL,
  SUPPORT_URL,
  BROWSER_EXTENSION_URL,
  HELP_URL,
  HOW_WE_RECYCLE_URL,
} from '../../config/externalURL';
import { getNavigationItems } from '../../utils/routes';
import useRewardsV2Flag from '../../utils/hooks/useRewardsV2Flag';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import { ROUTE_MY_ACCOUNT } from '../../config/routes';
import ACCOUNT_DRAWER_OPEN_EVENT, { ACCOUNT_DRAWER_STATE_EVENT } from '../../config/events';

import styles from './Footer.module.scss';

const {
  main,
  container,
  logo,
  img,
  resources,
  list,
  link,
  copy,
  footerColumn,
  columnTitle,
  columnsSection,
  linkList,
  socialNetworks,
} = styles;

const Footer = () => {
  const rewardsV2Enabled = useRewardsV2Flag();
  const mobile = isMobile();
  const navigateWithSearch = useNavigateWithSearch();
  const { pathname } = useLocation();
  const { userData } = useFirebaseAuth();
  const [isAccountDrawerOpen, setIsAccountDrawerOpen] = useState(false);

  useEffect(() => {
    const handleDrawerState = (event) => {
      setIsAccountDrawerOpen(Boolean(event?.detail?.isOpen));
    };

    window.addEventListener(ACCOUNT_DRAWER_STATE_EVENT, handleDrawerState);
    return () => window.removeEventListener(ACCOUNT_DRAWER_STATE_EVENT, handleDrawerState);
  }, []);

  return (
    <div className={main}>
      <div className={container}>
        {mobile ? (
          <BottomNavigation
            value={isAccountDrawerOpen ? ROUTE_MY_ACCOUNT : pathname}
            onChange={(_, route) => {
              if (route === ROUTE_MY_ACCOUNT) {
                setIsAccountDrawerOpen(true);
                window.dispatchEvent(new Event(ACCOUNT_DRAWER_OPEN_EVENT));
                return;
              }

              navigateWithSearch(route);
            }}
            showLabels
          >
            {getNavigationItems(
              rewardsV2Enabled,
              userData?.activeSubscription,
            ).map(({ label, icon: Icon, route }) => {
              const isAccountRoute = route === ROUTE_MY_ACCOUNT;
              const isActive = isAccountDrawerOpen
                ? isAccountRoute
                : route === pathname;

              return (
                <BottomNavigationAction
                  key={label}
                  label={label}
                  icon={<Icon height={24} width={24} color={isActive ? 'var(--trashie-primary-code-blue)' : undefined} />}
                  value={route}
                />
              );
            })}
          </BottomNavigation>
        ) : (
          <>
            <div className={columnsSection}>
              <div className={footerColumn}>
                <Link className={logo} to="/">
                  <img className={img} src={trashieLogo} alt="logo" />
                </Link>
              </div>
              <div className={footerColumn}>
                <h3 className={columnTitle}>
                  Trashie
                </h3>
                <ul className={linkList}>
                  <li>
                    <ExternalLink className={link} href={TAKE_BACK_BAG_URL}>
                      The take back bag
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink className={link} href={HOW_WE_RECYCLE_URL}>
                      How we recycle
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink className={link} href={BROWSER_EXTENSION_URL}>
                      The chrome extension
                    </ExternalLink>
                  </li>
                  <li>
                    <a
                      className={link}
                      href="#accessibility"
                      onClick={e => {
                        e.preventDefault();
                        window.interdeal?.a11y.openMenu();
                      }}
                    >
                      Accessibility
                    </a>
                  </li>
                </ul>
              </div>
              <div className={footerColumn}>
                <h3 className={columnTitle}>
                  Contact
                </h3>
                <ul className={linkList}>
                  <li>
                    <ExternalLink className={link} href={SUPPORT_URL} target="_blank" rel="noreferrer">
                      Support
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink className={link} href={HELP_URL} target="_blank" rel="noreferrer">
                      Help center
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink className={link} href={CONTACT_US_URL} target="_blank" rel="noreferrer">
                      Contact us
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink className={link} href={PARTNERSHIPS_URL} target="_blank" rel="noreferrer">
                      Partnerships
                    </ExternalLink>
                  </li>
                </ul>
              </div>
            </div>
            <div className={resources}>
              <div className={list}>
                <span className={copy}>
                  © 2025 Trashie. All rights reserved.
                </span>
                <ExternalLink className={link} href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
                  Privacy Policy
                </ExternalLink>
                <ExternalLink className={link} href={TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer">
                  Terms of Service
                </ExternalLink>
              </div>
              <div className={socialNetworks}>
                <a className={logo} href={TRASHIE_SOCIAL_NETWORKS.INSTAGRAM} target="_blank" rel="noreferrer">
                  <img className={img} src={instagramLogo} alt="instagram logo" />
                </a>
                <a className={logo} href={TRASHIE_SOCIAL_NETWORKS.TIKTOK} target="_blank" rel="noreferrer">
                  <img className={img} src={tiktokLogo} alt="tiktok logo" />
                </a>
                <a className={logo} href={TRASHIE_SOCIAL_NETWORKS.FACEBOOK} target="_blank" rel="noreferrer">
                  <img className={img} src={facebookLogo} alt="facebook logo" />
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Footer;
